import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Ellipsis, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'

// import { useSearchParams } from 'react-router'

import { AWXCardholderStatus } from '@ankor-io/common/types/awx'
import type { CardholderSummary } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import Loading from '@/ui-components/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'
import { Tooltip } from '@/ui-components/Tooltip'
import { IssueCardModal } from '@/views/components/IssueCardModal'

// import SimplePagination from '@/views/components/SimplePagination'

type CardholdersTableProps = {
  newCardholder: CardholderSummary | null
}

export const CardholdersTable = ({ newCardholder }: CardholdersTableProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [preselectedCardholder, setPreselectedCardholder] = useState<CardholderSummary | undefined>()
  const [cardholders, setCardholders] = useState<CardholderSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // const [totalPages, setTotalPages] = useState(1)
  // const [searchParams, setSearchParams] = useSearchParams()
  // const page = searchParams.get('page')
  // const itemsPerPage = 10
  useEffect(() => {
    const getCardholders = async () => {
      setIsLoading(true)
      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/cardholders`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch cardholders')
        }

        const data = await res.json()
        setCardholders(data.cardholders || [])
        // setTotalPages(Math.ceil((data.cardholders.length || 1) / itemsPerPage))
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }

    getCardholders()
  }, [getAccessToken, activeOrganizationId, activeConnectedAccountId])

  useEffect(() => {
    if (newCardholder) {
      setCardholders((prevCardholders) => [...prevCardholders, newCardholder])
    }
  }, [newCardholder])

  // const handlePaginate = (newPage: number) => {
  //   setSearchParams({ page: newPage.toString() })
  // }

  const openIssueCardModal = (cardholder: CardholderSummary) => {
    setPreselectedCardholder(cardholder)
    setIsModalOpen(true)
  }

  return (
    <>
      <Table className="w-[calc(100vw-3rem)] md:w-auto">
        <TableHead>
          <TableRow>
            <TableHeader>User</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <Loading />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {cardholders.map((cardholder) => (
                <TableRow
                  key={cardholder.id}
                  to={`/cards/cardholders/cardholder-details/${cardholder.id}`}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-x-1">
                        <UserRound className="size-3.5" />
                        <span className="text-sm font-medium">
                          {cardholder.identity.firstName} {cardholder.identity.lastName}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">{cardholder.identity.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {cardholder.status === AWXCardholderStatus.PENDING && (
                      <>
                        <Badge
                          color="yellow"
                          data-tooltip-id={`info-required-tooltip-${cardholder.id}`}
                          className="relative"
                        >
                          Info required
                        </Badge>
                        <Tooltip
                          id={`info-required-tooltip-${cardholder.id}`}
                          place="bottom"
                        >
                          Pending cardholder's profile info
                        </Tooltip>
                      </>
                    )}
                    {/* TODO: Add same logic for CardholdersDetails view */}
                    {cardholder.status === AWXCardholderStatus.READY && <Badge color="green">Verified</Badge>}
                    {cardholder.status === AWXCardholderStatus.DISABLED && <Badge color="red">Disabled</Badge>}
                    {cardholder.status === AWXCardholderStatus.INCOMPLETE && <Badge color="yellow">Incomplete</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="relative z-10 pointer-events-auto">
                      <Dropdown>
                        <DropdownButton plain>
                          <Ellipsis className="size-4" />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                          <DropdownItem onClick={() => openIssueCardModal(cardholder)}>Issue new card</DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {/* Empty state */}
              {cardholders.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center py-8"
                  >
                    <p className="text-gray-600 dark:text-gray-400">No cardholders found</p>
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
      {/* {cardholders.length > 0 && (
        <div className="flex justify-center mt-10">
          <SimplePagination
            totalPages={totalPages}
            currentPage={page ? parseInt(page) : 1}
            onPaginate={handlePaginate}
          />
        </div>
      )} */}

      <IssueCardModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        preselectedCardholder={preselectedCardholder}
      />
    </>
  )
}

export default CardholdersTable
